import { useStore } from 'effector-react';
import { css } from 'linaria';
import { sendEvent } from 'model/helpers/events.model';
import { lastScreen$ } from 'model/helpers/screens-history';
import { useLocalization } from 'model/localization.model';
import { h } from 'preact';
import { styled } from 'linaria/react';
import { FC } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { Button } from 'ui/atoms/button';

export type ExitPopupProps = {
	visible: boolean;
	onClose: () => void;
	onExit: () => void;
	overlay: boolean;
}

export const ExitPopup: FC<ExitPopupProps> = ({ visible, onClose, onExit, overlay }) => {
	const t = useLocalization();
	const lastScreen = useStore(lastScreen$);

	const [opacity, changeOpacity] = useState(0);
	const [display, changeDisplay] = useState(false);

	useEffect(() => {
		if (visible) {
			sendEvent({
				event: 'quit_session_opened',
				event_attributes: {
					screen: lastScreen,
				},
			});
			changeDisplay(true);
			setTimeout(changeOpacity, 10, 1);
		} else {
			changeOpacity(0);
			setTimeout(changeDisplay, 200, false);
		}
	}, [visible]);

	const handleContinue = () => {
		sendEvent({
			event: 'quit_session_cancelled',
			event_attributes: {
				screen: lastScreen,
			},
		});
		onClose();
	};
	const handleExit = () => {
		sendEvent({
			event: 'quit_session_confirmed',
			event_attributes: {
				screen: lastScreen,
			},
		});
		onExit();
	};

	return (
		<Overlay
			data-overlay={overlay}
			style={{
				opacity,
				display: display ? 'flex' : 'none',
			}}
		>
			<Wrapper>
				<Title>{t('flow.exit.title')}</Title>
				<Text>
					{t('flow.exit.description')}
				</Text>
				<ButtonsContainer>
					<Button
						name={'flow.exit.btn'}
						onClick={handleExit}
						className={btn}
						type={'outline'}
						style={{
							paddingLeft: 12,
							paddingRight: 12,
						}}
					>
						{t('flow.exit.btn')}
					</Button>
					<Button
						name={'flow.exit.btn.cancel'}
						onClick={handleContinue}
						className={btn}
						style={{
							paddingLeft: 8,
							paddingRight: 8,
						}}
					>
						{t('flow.exit.btn.cancel')}
					</Button>
				</ButtonsContainer>
			</Wrapper>
		</Overlay>
	);
};

const btn = css`
	min-height: 30px !important;
	
	span {
		font-size: 14px !important;
	}
`;
const ButtonsContainer = styled('div')`
	display: flex;
	justify-content: space-between;
`;
const Title = styled('h6')`
	margin: 0 0 4px 0;
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 24px;
	color: var(--text-primary);
`;
const Text = styled('p')`
	margin: 0 0 24px 0;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 22px;
	color: var(--text-secondary);
`;
const Wrapper = styled('div')`
	position: relative;
	z-index: 1;
	max-width: 240px;
	padding: 12px;
	background: var(--background);
	box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(136, 152, 170, 0.25), 0 15px 35px rgba(49, 49, 93, 0.15);
	border-radius: 4px;
`;
const Overlay = styled('div')`
	display: flex;
	justify-content: flex-end;
	align-items: flex-start;
	position: absolute;
	padding: 16px;
	left: 0;
	top: 0;
	bottom: 0;
	right: 0;
	background: transparent;
	border-radius: 4px;
	transition: opacity .2s ease-in-out;
	
	&[data-overlay='true'] {
		background: rgba(255, 255, 255, .6);
	}
`;
